import clamp from "lodash/clamp";
import { Length, Unit } from "../../types/customization/box";
import { Template, TemplateType } from "../../types/customization/template";

const maxZoomLevels = 2;
const defaultLimitSizes = {
  book: [150, 270],
  calendar: [600, 800],
  mural: [300, 600],
  print: [150, 300],
};

export const DEFAULT_ZOOM_LEVEL = 2;

export interface ZoomLimitPageSizes {
  max: Length<Unit.PX>;
  min: Length<Unit.PX>;
}

export function getZoom(currentLevel: number, modifier: number, nbPages: number): number {
  let level = currentLevel;
  if (nbPages < 2) {
    level = DEFAULT_ZOOM_LEVEL;
  }
  if (modifier < 0 && currentLevel > 1) {
    level = currentLevel + modifier;
  }
  if (modifier > 0 && currentLevel < maxZoomLevels) {
    level = currentLevel + modifier;
  }
  return clamp(level, 1, maxZoomLevels);
}

export function hasZoomButtonIn(nbPages: number, zoomLevel: number): boolean {
  if ((nbPages < 2 && zoomLevel === DEFAULT_ZOOM_LEVEL) || zoomLevel === maxZoomLevels) {
    return false;
  }
  return true;
}

export function hasZoomButtonOut(nbPages: number, zoomLevel: number): boolean {
  if (nbPages < 2 || zoomLevel === 1) {
    return false;
  }
  return true;
}

export function getZoomLimitPageSizes(
  zoomLevel: number,
  templateType: TemplateType
): ZoomLimitPageSizes {
  let minSize = 300;
  let maxSize = 600;
  const level = clamp(1, zoomLevel, maxZoomLevels) - 1;
  const limitSizes = defaultLimitSizes[templateType];
  minSize = limitSizes[level];
  maxSize = minSize * 2 - 1;

  return {
    max: { value: maxSize, unit: Unit.PX },
    min: { value: minSize, unit: Unit.PX },
  };
}

export function getPpmm(
  template: Template,
  zoomLevel: number,
  width: number,
  height: number,
  marginWidth: number,
  marginHeight: number,
  itemsMarginWidth: number,
  maxPageWidth: number,
  maxPageHeight: number,
  zoomLimitPageSizes: ZoomLimitPageSizes,
  limitedHeight = true
): number {
  const availableWidth = width - marginWidth;
  const availableHeight = height - marginHeight;

  const minZoomPageSizePx = zoomLimitPageSizes.min.value + (zoomLevel === 1 ? marginWidth : 0);
  const maxZoomPageSizePx = zoomLimitPageSizes.max.value + (zoomLevel === 1 ? marginWidth : 0);

  let nbPagesLinked = 1;
  let nbPagesZoomMin = availableWidth / minZoomPageSizePx;
  const nbPagesWithZoom = Math.floor(nbPagesZoomMin);
  const nbPagesZoomMax = availableWidth / maxZoomPageSizePx;
  // temporary conditions for book : to change with precisions on zoom behaviour
  if (template.templateType === "book") {
    nbPagesZoomMin = zoomLevel === 2 ? 1 : Math.min(nbPagesZoomMin, 2);
  }
  if (template.templateType === "book" && template.pageMode === "single_horizontal") {
    nbPagesLinked = 2;
  }
  const zoomMinPpmmWidth =
    (availableWidth - nbPagesZoomMin * itemsMarginWidth) / (nbPagesZoomMin * maxPageWidth);
  const zoomMaxPpmmWidth =
    (availableWidth - nbPagesZoomMax * itemsMarginWidth) / (nbPagesZoomMax * maxPageWidth);
  const zoomPpmmWidth =
    (availableWidth - nbPagesWithZoom * itemsMarginWidth) / (nbPagesWithZoom * maxPageWidth);

  const ppmmWidth = clamp(zoomPpmmWidth, zoomMinPpmmWidth, zoomMaxPpmmWidth);
  const ppmmWidthMax = availableWidth / maxPageWidth;
  const ppmmZoomLimitMax = zoomLimitPageSizes.max.value / nbPagesLinked / maxPageWidth;

  let ppmmMin: number;
  if (limitedHeight === false) {
    ppmmMin = Math.min(ppmmWidth, ppmmWidthMax, ppmmZoomLimitMax);
  } else {
    const ppmmHeightMax = availableHeight / maxPageHeight;
    ppmmMin = Math.min(ppmmWidth, ppmmHeightMax, ppmmWidthMax, ppmmZoomLimitMax);
  }

  return ppmmMin;
}
